import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactCountryFlag from "react-country-flag";
import { toast } from "react-toastify";
import ApiService from "../services/ApiService";
import { setSelectedCurrency } from "../store/actions/currencyActions";
import currencyCountries from "../utils/currencyCountries.json";

const Settings = ({ currency: { currencies, selectedCurrency }, setSelectedCurrency }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Get current user preferences
  const { data: userCurrency, isLoading: isCurrencyLoading } = useQuery({
    queryKey: ["userCurrency"],
    queryFn: ApiService.getUserCurrency,
    select: (data) => data.data,
  });

  const { data: userLanguage, isLoading: isLanguageLoading } = useQuery({
    queryKey: ["userLanguage"],
    queryFn: ApiService.getLang,
    select: (data) => data.data,
  });

  // Mutations for updating preferences
  const currencyMutation = useMutation({
    mutationFn: (currency) => ApiService.changeCurrency(currency),
    onSuccess: () => {
      toast.success(t("currency_updated_successfully"));
      queryClient.invalidateQueries(["userCurrency"]);
    },
    onError: () => {
      toast.error(t("currency_update_failed"));
    },
  });

  const languageMutation = useMutation({
    mutationFn: (language) => ApiService.changeLang(language),
    onSuccess: () => {
      toast.success(t("language_updated_successfully"));
      queryClient.invalidateQueries(["userLanguage"]);
    },
    onError: () => {
      toast.error(t("language_update_failed"));
    },
  });

  const handleCurrencyChange = (event) => {
    const selectedCurrencyCode = event.target.value;
    console.log('User selected currency:', selectedCurrencyCode);
    
    // Find the currency object with conversion rate
    const currencyObject = currencies.find(
      currency => currency.currency === selectedCurrencyCode
    );
    
    if (currencyObject) {
      // Update Redux state immediately for UI feedback
      setSelectedCurrency(currencyObject);
      // Update backend preference
      currencyMutation.mutate(selectedCurrencyCode);
    }
  };

  const handleLanguageChange = (event) => {
    const selectedLanguage = event.target.value;
    console.log('User selected language:', selectedLanguage);
    
    // Update i18n immediately for UI feedback
    i18n.changeLanguage(selectedLanguage === 'no' ? 'no' : 'en');
    // Update backend preference
    languageMutation.mutate(selectedLanguage);
  };

  const availableLanguages = [
    { code: 'no', name: 'Norsk', flag: 'NO' },
    { code: 'en', name: 'English', flag: 'GB' },
  ];

  return (
    <Container fluid className="pt-4">
      <Row>
        <Col xl={8} lg={10} className="mx-auto">
          <Card>
            <CardHeader>
              <h4 className="card-title mb-0">
                {t("Settings")}
              </h4>
            </CardHeader>
            <CardBody>
              <Form>
                <Row>
                  <Col md={6}>
                    <Card className="mb-4">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("Language")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <FormGroup>
                          <Label for="languageSelect">
                            {t("Select your preferred language")}
                          </Label>
                          {isLanguageLoading ? (
                            <div className="d-flex align-items-center">
                              <Spinner size="sm" className="me-2" />
                              {t("Loading...")}
                            </div>
                          ) : (
                            <Input
                              type="select"
                              id="languageSelect"
                              value={userLanguage || 'no'}
                              onChange={handleLanguageChange}
                              disabled={languageMutation.isLoading}
                            >
                              {availableLanguages.map((lang) => (
                                <option key={lang.code} value={lang.code}>
                                  {lang.name}
                                </option>
                              ))}
                            </Input>
                          )}
                        </FormGroup>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="mb-4">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("Currency")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <FormGroup>
                          <Label for="currencySelect">
                            {t("Select your preferred currency")}
                          </Label>
                          {isCurrencyLoading ? (
                            <div className="d-flex align-items-center">
                              <Spinner size="sm" className="me-2" />
                              {t("Loading...")}
                            </div>
                          ) : (
                            <Input
                              type="select"
                              id="currencySelect"
                              value={userCurrency || 'NOK'}
                              onChange={handleCurrencyChange}
                              disabled={currencyMutation.isLoading}
                            >
                              {currencies.map((currency) => (
                                <option key={currency.currency} value={currency.currency}>
                                  <ReactCountryFlag 
                                    svg 
                                    countryCode={currency.country} 
                                    style={{ marginRight: '8px' }}
                                  />
                                  {currency.currency}
                                </option>
                              ))}
                            </Input>
                          )}
                        </FormGroup>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {(currencyMutation.isLoading || languageMutation.isLoading) && (
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Spinner size="sm" className="me-2" />
                    {t("Saving changes...")}
                  </div>
                )}
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currency: state.currency,
});

export default connect(mapStateToProps, { setSelectedCurrency })(Settings);